import React, { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import { FaFilterIcon } from "../../../assets/Images/icon";
import { formatDate, pillClass, toastError, toastSuccess } from "../helpers";
import usePagedList from "./usePagedList";
import { LoadingRow, NoteModal, PerPageSelect, SimplePager } from "./Shared";

const keyState = (key) => (key.is_active ? "active" : key.revoked_at ? "revoked" : "expired");

/**
 * Keys across dealerships. The secret is never listed: support finds a key from a ticket by its
 * 12-character prefix (keyword). Revoking is immediate on the server.
 */
const KeysTab = ({ access, superAdmin, onIssueKey, refreshToken }) => {
  const [type, _type] = useState("");
  const [includeRevoked, _includeRevoked] = useState(false);
  const [keywordInput, _keywordInput] = useState("");
  const [keyword, _keyword] = useState("");
  const [perPage, _perPage] = useState("");
  const [revoking, _revoking] = useState(null);
  const [busy, _busy] = useState(false);

  const list = usePagedList("/keys", {
    "filter[type]": type,
    include_revoked: includeRevoked ? 1 : "",
    keyword,
    per_page: perPage,
  }, refreshToken); // bumped by the page when a key is issued from another tab

  const clearFilter = () => {
    _type("");
    _includeRevoked(false);
    _keywordInput("");
    _keyword("");
    _perPage("");
  };

  const revoke = () => {
    _busy(true);
    API.post(`${APP_URLS.DEALER_API}/keys/${revoking.id}/revoke`)
      .then((res) => {
        if (res.data.success === true) {
          toastSuccess(res.data.message);
          _revoking(null);
          list.reload();
        } else {
          toastError({ message: res.data.message });
        }
      })
      .catch(toastError)
      .finally(() => _busy(false));
  };

  return (
    <>
      <div className="filters">
        <select className="filter-select" value={type} onChange={(e) => _type(e.target.value)}>
          <option value="">All keys</option>
          <option value="master">Master keys</option>
          <option value="sub">Supplier keys</option>
        </select>
        <label className="d-flex align-items-center gap-1 mb-0">
          <input type="checkbox" checked={includeRevoked} onChange={(e) => _includeRevoked(e.target.checked)} />
          Include revoked
        </label>
        <input
          className="filter-input"
          placeholder="Key prefix, label or dealership"
          maxLength={100}
          value={keywordInput}
          onChange={(e) => _keywordInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && _keyword(keywordInput.trim())}
        />
        <button className="btn btn-secondary" onClick={() => _keyword(keywordInput.trim())}>
          Search
        </button>
        <PerPageSelect value={perPage} onChange={_perPage} />
        <Tooltip title="Clear Filter" placement="bottom">
          <FaFilterIcon className="filter-icon" role="button" onClick={clearFilter} />
        </Tooltip>
      </div>

      <div className="tableContainer">
        <table className="auctionTable">
          <thead className="tableHead">
            <tr className="table-row">
              <th className="time-start">Dealership</th>
              <th className="time-start">Key</th>
              <th className="time-start">Created</th>
              <th className="time-start">Last used</th>
              <th className="time-start">Expires</th>
              <th className="time-start">Status</th>
              <th className="time-start">Actions</th>
            </tr>
          </thead>
          {list.loading ? (
            <LoadingRow colSpan={7} />
          ) : (
            <tbody>
              {list.rows.length > 0 ? (
                list.rows.map((key) => (
                  <tr key={`key-${key.id}`} className="table-row">
                    <td>
                      <b>{key.subsidiary?.name ?? `#${key.subsidiary_id}`}</b>
                      <div className="muted">#{key.subsidiary_id}</div>
                    </td>
                    <td>
                      <span className="mono">{key.masked_key}</span>
                      <div className="muted">
                        {key.type === "master" ? "Master (read + write)" : "Supplier (read only)"}
                        {key.label && key.type !== "master" ? ` · ${key.label}` : ""}
                      </div>
                    </td>
                    <td>
                      {formatDate(key.created_at)}
                      <div className="muted">
                        {key.created_via === "admin" ? "by Cartobike support" : "by the dealer"}
                        {key.created_by ? ` (${key.created_by.name})` : ""}
                      </div>
                    </td>
                    <td>{formatDate(key.last_used_at)}</td>
                    <td>{key.expires_at ? formatDate(key.expires_at) : "Never"}</td>
                    <td>
                      <span className={pillClass(keyState(key))}>{keyState(key)}</span>
                      {key.revoked_at && (
                        <div className="muted">
                          {formatDate(key.revoked_at)}
                          {key.revoked_by ? ` by ${key.revoked_by.name}` : ""}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-btns">
                        {key.is_active && access.canUpdate && (
                          <Button variant="contained" className="bg-danger" onClick={() => _revoking(key)}>
                            Revoke
                          </Button>
                        )}
                        {key.type === "master" && access.canAdd && superAdmin && (
                          <Button variant="outlined" onClick={() => onIssueKey(key.subsidiary ?? { id: key.subsidiary_id, name: `#${key.subsidiary_id}` })}>
                            Issue new master key
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <NoRecord />
              )}
            </tbody>
          )}
        </table>
      </div>
      <SimplePager page={list.page} hasPrev={list.hasPrev} hasNext={list.hasNext} onChange={list.setPage} />

      <NoteModal
        open={revoking !== null}
        title="Revoke this key"
        message={
          revoking
            ? `${revoking.masked_key} stops working on its next request. ${
                revoking.type === "master" ? "The dealership's DMS can no longer push until a new master key is generated." : ""
              }`
            : ""
        }
        withNote={false}
        confirmLabel="Revoke key"
        danger
        busy={busy}
        onConfirm={revoke}
        onClose={() => _revoking(null)}
      />
    </>
  );
};

export default KeysTab;
